import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import parse from 'html-react-parser';
import AppLink from './AppLink';
import Button from './Button';
import Image from './Image';
import { runtime } from '../inc/Runtime';
import { scroller } from '../inc/Scroller';

const DRAWER_ANIMATION_DURATION = 260;
const HEADER_SCROLL_TOP_THRESHOLD = 4;
const HEADER_SCROLL_DIRECTION_THRESHOLD = 3;

const isExternal = (url = '') => {
    return /^https?:\/\//i.test(url) || url.startsWith('mailto:') || url.startsWith('tel:');
};

const getUrlOrigin = (url = '', base = '') => {
    try{
        return new URL(url, base || undefined).origin;
    } catch(_error){
        return '';
    }
};

const isSameSiteUrl = (url = '') => {
    if(!/^https?:\/\//i.test(url)){
        return false;
    }

    const fallbackBase = runtime.system?.homeUrl || runtime.system?.baseUrl || runtime.site?.homeUrl || 'http://localhost/';
    const destinationOrigin = getUrlOrigin(url, fallbackBase);
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const allowedOrigins = [
        currentOrigin,
        getUrlOrigin(runtime.system?.homeUrl || '', fallbackBase),
        getUrlOrigin(runtime.system?.baseUrl || '', fallbackBase),
        getUrlOrigin(runtime.site?.homeUrl || '', fallbackBase),
    ].filter(Boolean);

    return destinationOrigin !== '' && allowedOrigins.includes(destinationOrigin);
};

const sameSitePathFromUrl = (url = '') => {
    try{
        const base = runtime.system?.homeUrl || runtime.system?.baseUrl || runtime.site?.homeUrl || 'http://localhost/';
        const destination = new URL(url, base);

        return `${destination.pathname || '/'}${destination.search || ''}${destination.hash || ''}`;
    } catch(_error){
        return '';
    }
};

const getColorGroup = (slug = '') => {
    const groups = runtime.system?.zeroUn?.colorGroups || [];

    if(!Array.isArray(groups) || !groups.length){
        return null;
    }

    const normalizedSlug = String(slug || '').trim();

    return groups.find((group) => group?.slug === normalizedSlug)
        || groups.find((group) => group?.slug === 'default')
        || groups[0]
        || null;
};

const buildButtonColorVariables = (slug = '') => {
    const colorGroup = getColorGroup(slug || 'default');

    if(!colorGroup || typeof colorGroup !== 'object'){
        return {};
    }

    const colors = colorGroup.colors || colorGroup;
    const button = colors.button || {};
    const primaryButton = button.primary || button || {};
    const secondaryButton = button.secondary || {};
    const textButton = button.text || {};
    const base = colors.base || {};

    return {
        '--zero-un-header-button-primary-color': primaryButton.text || undefined,
        '--zero-un-header-button-primary-bg': primaryButton.background || base.accent || undefined,
        '--zero-un-header-button-primary-border': primaryButton.border || primaryButton.background || base.accent || undefined,
        '--zero-un-header-button-primary-hover-color': primaryButton.hover_text || undefined,
        '--zero-un-header-button-primary-hover-bg': primaryButton.hover_background || undefined,
        '--zero-un-header-button-primary-hover-border': primaryButton.hover_border || primaryButton.hover_background || undefined,
        '--zero-un-header-button-secondary-color': secondaryButton.text || primaryButton.background || base.accent || undefined,
        '--zero-un-header-button-secondary-bg': secondaryButton.background || 'transparent',
        '--zero-un-header-button-secondary-border': secondaryButton.border || secondaryButton.text || primaryButton.background || base.accent || undefined,
        '--zero-un-header-button-secondary-hover-color': secondaryButton.hover_text || undefined,
        '--zero-un-header-button-secondary-hover-bg': secondaryButton.hover_background || undefined,
        '--zero-un-header-button-secondary-hover-border': secondaryButton.hover_border || secondaryButton.hover_background || undefined,
        '--zero-un-header-button-text-color': textButton.text || secondaryButton.text || primaryButton.background || base.accent || undefined,
        '--zero-un-header-button-text-bg': textButton.background || 'transparent',
        '--zero-un-header-button-text-border': textButton.border || 'transparent',
        '--zero-un-header-button-text-hover-color': textButton.hover_text || secondaryButton.hover_text || undefined,
        '--zero-un-header-button-text-hover-bg': textButton.hover_background || 'transparent',
        '--zero-un-header-button-text-hover-border': textButton.hover_border || 'transparent',
    };
};

const buildConfiguredButtonColorVariables = (colors = {}) => {
    if(!colors || typeof colors !== 'object'){
        return {};
    }

    return {
        '--zero-un-header-button-primary-color': colors.primaryColor,
        '--zero-un-header-button-primary-bg': colors.primaryBackground,
        '--zero-un-header-button-primary-border': colors.primaryBorder,
        '--zero-un-header-button-primary-hover-color': colors.primaryHoverColor,
        '--zero-un-header-button-primary-hover-bg': colors.primaryHoverBackground,
        '--zero-un-header-button-primary-hover-border': colors.primaryHoverBorder,
        '--zero-un-header-button-secondary-color': colors.secondaryColor,
        '--zero-un-header-button-secondary-bg': colors.secondaryBackground,
        '--zero-un-header-button-secondary-border': colors.secondaryBorder,
        '--zero-un-header-button-secondary-hover-color': colors.secondaryHoverColor,
        '--zero-un-header-button-secondary-hover-bg': colors.secondaryHoverBackground,
        '--zero-un-header-button-secondary-hover-border': colors.secondaryHoverBorder,
        '--zero-un-header-button-text-color': colors.textColor,
        '--zero-un-header-button-text-bg': colors.textBackground,
        '--zero-un-header-button-text-border': colors.textBorder,
        '--zero-un-header-button-text-hover-color': colors.textHoverColor,
        '--zero-un-header-button-text-hover-bg': colors.textHoverBackground,
        '--zero-un-header-button-text-hover-border': colors.textHoverBorder,
    };
};

const getSafeSvgMarkup = (value = '') => {
    const markup = String(value || '').trim();

    if(!markup || !/^<svg[\s>]/i.test(markup)){
        return '';
    }

    return markup
        .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
        .replace(/\son[a-z]+\s*=\s*(['"]).*?\1/gi, '')
        .replace(/\s(href|xlink:href)\s*=\s*(['"])\s*javascript:[\s\S]*?\2/gi, '');
};

const styleVariables = (config = {}) => {
    const colors = config.colors || {};
    const languageSwitcher = config.languageSwitcher || {};
    const languageDropdown = languageSwitcher.dropdown || {};
    const navigationTypography = config.navigationTypography || {};
    const buttonStyle = config.buttonStyle || {};
    const hamburgerColors = config.hamburger?.colors || {};
    const hamburgerRadii = config.hamburger?.radii || {};
    const hamburgerPanelCta = config.hamburger?.panelCta || {};
    const hamburgerSpotlightCta = config.hamburger?.spotlightCta || {};
    const hamburgerCtaIcon = config.hamburger?.ctaIcon || {};
    const hamburgerTypography = config.hamburger?.typography || {};

    return Object.fromEntries(Object.entries({
        '--zero-un-header-bg': colors.background,
        '--zero-un-header-radius': config.borderRadiusValue,
        '--zero-un-header-logo-width': config.logoWidth,
        '--zero-un-header-link': colors.link,
        '--zero-un-header-link-hover': colors.linkHover,
        '--zero-un-header-submenu-bg': colors.submenuBackground,
        '--zero-un-header-submenu-link': colors.submenuLink,
        '--zero-un-header-submenu-link-hover': colors.submenuLinkHover,
        '--zero-un-header-submenu-border': colors.submenuBorder,
        '--zero-un-header-hamburger': colors.hamburger,
        '--zero-un-header-hamburger-hover': colors.hamburgerHover,
        '--zero-un-header-language-bg': colors.languageSwitcherBackground,
        '--zero-un-header-language-border': colors.languageSwitcherBorder,
        '--zero-un-header-language-text': colors.languageSwitcherText,
        '--zero-un-header-language-arrow': colors.languageSwitcherArrow,
        '--zero-un-header-language-hover-bg': colors.languageSwitcherHoverBackground,
        '--zero-un-header-language-hover-border': colors.languageSwitcherHoverBorder,
        '--zero-un-header-language-hover-text': colors.languageSwitcherHoverText,
        '--zero-un-header-language-hover-arrow': colors.languageSwitcherHoverArrow,
        '--zero-un-header-language-height': languageSwitcher.height,
        '--zero-un-header-language-radius': languageSwitcher.borderRadius,
        '--zero-un-header-language-dropdown-font-size': languageDropdown.fontSize,
        '--zero-un-header-language-dropdown-font-weight': languageDropdown.fontWeight,
        '--zero-un-header-language-dropdown-padding': languageDropdown.padding,
        '--zero-un-header-language-dropdown-radius': languageDropdown.borderRadius,
        '--zero-un-header-language-dropdown-bg': colors.languageSwitcherDropdownBackground,
        '--zero-un-header-language-dropdown-border': colors.languageSwitcherDropdownBorder,
        '--zero-un-header-language-dropdown-item': colors.languageSwitcherDropdownItem,
        '--zero-un-header-language-dropdown-hover-bg': colors.languageSwitcherDropdownHoverBackground,
        '--zero-un-header-language-dropdown-hover-border': colors.languageSwitcherDropdownHoverBorder,
        '--zero-un-header-language-dropdown-item-hover-bg': colors.languageSwitcherDropdownItemHoverBackground,
        '--zero-un-header-language-dropdown-item-hover': colors.languageSwitcherDropdownItemHover,
        '--zero-un-header-nav-font-size': navigationTypography.fontSize,
        '--zero-un-header-nav-line-height': navigationTypography.lineHeight,
        '--zero-un-header-nav-font-weight': navigationTypography.fontWeight,
        '--zero-un-header-nav-mobile-font-size': navigationTypography.mobileFontSize,
        '--zero-un-header-nav-mobile-line-height': navigationTypography.mobileLineHeight,
        '--zero-un-header-button-height': buttonStyle.height,
        '--zero-un-header-button-border-radius': buttonStyle.borderRadius,
        '--zero-un-header-action-menu-gap': buttonStyle.actionMenuGap,
        '--zero-un-header-action-edge-padding': buttonStyle.actionEdgePadding,
        '--zero-un-header-action-gap': buttonStyle.actionGap,
        '--zero-un-hamburger-bg': hamburgerColors.background,
        '--zero-un-hamburger-uptitle': hamburgerColors.uptitle,
        '--zero-un-hamburger-title': hamburgerColors.title,
        '--zero-un-hamburger-title-separator': hamburgerColors.titleSeparator,
        '--zero-un-hamburger-link': hamburgerColors.link,
        '--zero-un-hamburger-link-hover': hamburgerColors.linkHover,
        '--zero-un-hamburger-link-box-bg': hamburgerColors.linkBoxBackground,
        '--zero-un-hamburger-link-box-border': hamburgerColors.linkBoxBorder,
        '--zero-un-hamburger-link-box-hover-bg': hamburgerColors.linkBoxHoverBackground,
        '--zero-un-hamburger-link-box-hover-border': hamburgerColors.linkBoxHoverBorder,
        '--zero-un-hamburger-submenu-link': hamburgerColors.submenuLink,
        '--zero-un-hamburger-submenu-link-hover': hamburgerColors.submenuLinkHover,
        '--zero-un-hamburger-button': hamburgerColors.button,
        '--zero-un-hamburger-button-hover': hamburgerColors.buttonHover,
        '--zero-un-hamburger-panel-radius': hamburgerRadii.panel,
        '--zero-un-hamburger-cta-radius': hamburgerRadii.cta,
        '--zero-un-hamburger-cta-icon-radius': hamburgerRadii.ctaIcon,
        '--zero-un-hamburger-font-size': hamburgerTypography.fontSize,
        '--zero-un-hamburger-line-height': hamburgerTypography.lineHeight,
        '--zero-un-hamburger-font-weight': hamburgerTypography.fontWeight,
        '--zero-un-hamburger-mobile-font-size': hamburgerTypography.mobileFontSize,
        '--zero-un-hamburger-mobile-line-height': hamburgerTypography.mobileLineHeight,
        '--zero-un-hamburger-uptitle-font-size': hamburgerTypography.uptitleFontSize,
        '--zero-un-hamburger-uptitle-line-height': hamburgerTypography.uptitleLineHeight,
        '--zero-un-hamburger-title-font-size': hamburgerTypography.titleFontSize,
        '--zero-un-hamburger-title-line-height': hamburgerTypography.titleLineHeight,
        '--zero-un-hamburger-spotlight-title-font-size': hamburgerTypography.spotlightTitleFontSize,
        '--zero-un-hamburger-spotlight-title-line-height': hamburgerTypography.spotlightTitleLineHeight,
        '--zero-un-hamburger-spotlight-link-font-size': hamburgerTypography.spotlightLinkFontSize,
        '--zero-un-hamburger-spotlight-link-line-height': hamburgerTypography.spotlightLinkLineHeight,
        '--zero-un-hamburger-spotlight-link-font-weight': hamburgerTypography.spotlightLinkFontWeight,
        '--zero-un-hamburger-spotlight-submenu-font-size': hamburgerTypography.spotlightSubmenuFontSize,
        '--zero-un-hamburger-spotlight-submenu-line-height': hamburgerTypography.spotlightSubmenuLineHeight,
        '--zero-un-hamburger-spotlight-submenu-font-weight': hamburgerTypography.spotlightSubmenuFontWeight,
        '--zero-un-hamburger-spotlight-contact-font-size': hamburgerTypography.spotlightContactFontSize,
        '--zero-un-hamburger-spotlight-contact-line-height': hamburgerTypography.spotlightContactLineHeight,
        '--zero-un-hamburger-spotlight-contact-font-weight': hamburgerTypography.spotlightContactFontWeight,
        '--zero-un-hamburger-spotlight-cta-font-size': hamburgerTypography.spotlightCtaFontSize,
        '--zero-un-hamburger-spotlight-cta-line-height': hamburgerTypography.spotlightCtaLineHeight,
        '--zero-un-hamburger-spotlight-cta-font-weight': hamburgerTypography.spotlightCtaFontWeight,
        '--zero-un-hamburger-spotlight-cta-width': hamburgerSpotlightCta.width,
        '--zero-un-hamburger-spotlight-cta-aspect-ratio': hamburgerSpotlightCta.aspectRatio,
        '--zero-un-hamburger-panel-cta-width': hamburgerPanelCta.width,
        '--zero-un-hamburger-panel-cta-aspect-ratio': hamburgerPanelCta.aspectRatio,
        '--zero-un-hamburger-cta-bg': hamburgerColors.ctaBackground,
        '--zero-un-hamburger-cta-border': hamburgerColors.ctaBorder,
        '--zero-un-hamburger-cta-text': hamburgerColors.ctaText,
        '--zero-un-hamburger-cta-icon-bg': hamburgerColors.ctaIconBackground,
        '--zero-un-hamburger-cta-icon-border': hamburgerColors.ctaIconBorder,
        '--zero-un-hamburger-cta-icon-color': hamburgerColors.ctaIconColor,
        '--zero-un-hamburger-cta-icon-width': hamburgerCtaIcon.width,
        '--zero-un-hamburger-cta-icon-aspect-ratio': hamburgerCtaIcon.aspectRatio,
        '--zero-un-hamburger-cta-icon-inner-width': hamburgerCtaIcon.innerWidth,
        '--zero-un-hamburger-cta-hover-bg': hamburgerColors.ctaHoverBackground,
        '--zero-un-hamburger-cta-hover-border': hamburgerColors.ctaHoverBorder,
        '--zero-un-hamburger-cta-hover-text': hamburgerColors.ctaHoverText,
        '--zero-un-hamburger-cta-icon-hover-bg': hamburgerColors.ctaIconHoverBackground,
        '--zero-un-hamburger-cta-icon-hover-border': hamburgerColors.ctaIconHoverBorder,
        '--zero-un-hamburger-cta-icon-hover-color': hamburgerColors.ctaIconHoverColor,
        '--zero-un-hamburger-submenu-font-size': hamburgerTypography.submenuFontSize,
        '--zero-un-hamburger-submenu-line-height': hamburgerTypography.submenuLineHeight,
        '--zero-un-hamburger-submenu-font-weight': hamburgerTypography.submenuFontWeight,
        '--zero-un-hamburger-index-font-size': hamburgerTypography.indexFontSize,
        '--zero-un-hamburger-index-line-height': hamburgerTypography.indexLineHeight,
        '--zero-un-hamburger-mobile-uptitle-font-size': hamburgerTypography.mobileUptitleFontSize,
        '--zero-un-hamburger-mobile-uptitle-line-height': hamburgerTypography.mobileUptitleLineHeight,
        '--zero-un-hamburger-mobile-title-font-size': hamburgerTypography.mobileTitleFontSize,
        '--zero-un-hamburger-mobile-title-line-height': hamburgerTypography.mobileTitleLineHeight,
        '--zero-un-hamburger-mobile-spotlight-title-font-size': hamburgerTypography.mobileSpotlightTitleFontSize,
        '--zero-un-hamburger-mobile-spotlight-title-line-height': hamburgerTypography.mobileSpotlightTitleLineHeight,
        '--zero-un-hamburger-mobile-spotlight-link-font-size': hamburgerTypography.mobileSpotlightLinkFontSize,
        '--zero-un-hamburger-mobile-spotlight-link-line-height': hamburgerTypography.mobileSpotlightLinkLineHeight,
        '--zero-un-hamburger-mobile-spotlight-submenu-font-size': hamburgerTypography.mobileSpotlightSubmenuFontSize,
        '--zero-un-hamburger-mobile-spotlight-submenu-line-height': hamburgerTypography.mobileSpotlightSubmenuLineHeight,
        '--zero-un-hamburger-mobile-spotlight-contact-font-size': hamburgerTypography.mobileSpotlightContactFontSize,
        '--zero-un-hamburger-mobile-spotlight-contact-line-height': hamburgerTypography.mobileSpotlightContactLineHeight,
        '--zero-un-hamburger-mobile-spotlight-cta-font-size': hamburgerTypography.mobileSpotlightCtaFontSize,
        '--zero-un-hamburger-mobile-spotlight-cta-line-height': hamburgerTypography.mobileSpotlightCtaLineHeight,
        '--zero-un-hamburger-mobile-submenu-font-size': hamburgerTypography.mobileSubmenuFontSize,
        '--zero-un-hamburger-mobile-submenu-line-height': hamburgerTypography.mobileSubmenuLineHeight,
        '--zero-un-hamburger-mobile-index-font-size': hamburgerTypography.mobileIndexFontSize,
        '--zero-un-hamburger-mobile-index-line-height': hamburgerTypography.mobileIndexLineHeight,
        ...buildButtonColorVariables(config.buttonColorGroup),
        ...buildConfiguredButtonColorVariables(config.buttonColors),
    }).filter(([, value]) => value));
};

const resolveMenu = (navigation = {}, location = '') => {
    if(!location || !navigation || typeof navigation !== 'object'){
        return [];
    }

    const menu = navigation[location];

    return Array.isArray(menu) ? menu : [];
};

const resolveFirstMenu = (navigation = {}) => {
    if(!navigation || typeof navigation !== 'object'){
        return [];
    }

    return Object.values(navigation).find((menu) => Array.isArray(menu) && menu.length > 0) || [];
};

const normalizeLanguage = (value = '') => {
    const language = String(value || '').toLowerCase().trim().replace(/_/g, '-');

    return language || '';
};

const configuredHeaderLanguages = (config = {}) => {
    const values = [
        ...(Array.isArray(config.languages) ? config.languages.map((item) => item?.code || item?.slug) : []),
        ...Object.keys(config.homeUrls && typeof config.homeUrls === 'object' ? config.homeUrls : {}),
        ...Object.keys(config.logos && typeof config.logos === 'object' ? config.logos : {}),
        ...(Array.isArray(config.languageSwitcher?.items) ? config.languageSwitcher.items.map((item) => item?.code) : []),
    ];

    return [...new Set(values.map(normalizeLanguage).filter(Boolean).flatMap((language) => [
        language,
        language.split('-')[0],
    ]))];
};

const headerLanguageFromPath = (path = '', config = {}) => {
    const firstSegment = String(path || '')
        .split('?')[0]
        .split('#')[0]
        .split('/')
        .filter(Boolean)[0];
    const language = normalizeLanguage(firstSegment);
    const availableLanguages = configuredHeaderLanguages(config);

    return language && availableLanguages.includes(language) ? language : '';
};

const currentHeaderLanguage = (config = {}, route = null) => {
    const currentLanguageItem = Array.isArray(config.languageSwitcher?.items)
        ? config.languageSwitcher.items.find((item) => item?.current)
        : null;
    const candidates = [
        headerLanguageFromPath(route?.path || route?.pathname || '', config),
        typeof window !== 'undefined' ? headerLanguageFromPath(window.location.pathname, config) : '',
        route?.language,
        route?.lang,
        route?.locale,
        route?.data?.language,
        route?.data?.lang,
        currentLanguageItem?.code,
        typeof document !== 'undefined' ? document.documentElement?.lang : '',
        config.language,
        runtime.system?.language,
        runtime.system?.currentLanguage,
        runtime.system?.currentLang,
        runtime.site?.language,
        runtime.site?.lang,
    ];

    return candidates.map(normalizeLanguage).find(Boolean) || 'fr';
};

const resolveHeaderLogo = (config = {}, language = '') => {
    const logos = config.logos && typeof config.logos === 'object' && !Array.isArray(config.logos)
        ? config.logos
        : {};
    const normalizedLanguage = normalizeLanguage(language);
    const shortLanguage = normalizedLanguage.split('-')[0] || '';
    const preferredKeys = [normalizedLanguage, shortLanguage].filter(Boolean);

    for(const key of preferredKeys){
        if(logos[key]?.url){
            return {
                logo: logos[key],
                mediaKey: `header-logo-${key}`,
            };
        }
    }

    const fallbackEntry = Object.entries(logos).find(([, logo]) => logo?.url);

    if(fallbackEntry){
        return {
            logo: fallbackEntry[1],
            mediaKey: `header-logo-${fallbackEntry[0]}`,
        };
    }

    return {
        logo: config.logo || null,
        mediaKey: 'header-logo-default',
    };
};

const resolveHeaderHomeUrl = (config = {}, language = '') => {
    const homeUrls = config.homeUrls && typeof config.homeUrls === 'object' && !Array.isArray(config.homeUrls)
        ? config.homeUrls
        : {};
    const normalizedLanguage = normalizeLanguage(language);
    const shortLanguage = normalizedLanguage.split('-')[0] || '';

    return homeUrls[normalizedLanguage]
        || homeUrls[shortLanguage]
        || config.homeUrl
        || runtime.site?.homeUrl
        || runtime.system?.homeUrl
        || '/';
};

const resolveNavigationLocation = (locations = {}, language = '', fallback = '') => {
    if(!locations || typeof locations !== 'object' || Array.isArray(locations)){
        return fallback || '';
    }

    const normalizedLanguage = normalizeLanguage(language);
    const shortLanguage = normalizedLanguage.split('-')[0] || '';

    if(normalizedLanguage && locations[normalizedLanguage]){
        return locations[normalizedLanguage];
    }

    if(shortLanguage && locations[shortLanguage]){
        return locations[shortLanguage];
    }

    const firstLocation = Object.values(locations).find(Boolean);

    return firstLocation || fallback || '';
};

const hasNavigationLocation = (location = '') => String(location || '').trim() !== '';

const resolveHeaderButtons = (buttonsByLanguage = {}, language = '', fallback = []) => {
    if(buttonsByLanguage && typeof buttonsByLanguage === 'object' && !Array.isArray(buttonsByLanguage)){
        const normalizedLanguage = normalizeLanguage(language);
        const shortLanguage = normalizedLanguage.split('-')[0] || '';

        if(normalizedLanguage && Array.isArray(buttonsByLanguage[normalizedLanguage])){
            return buttonsByLanguage[normalizedLanguage];
        }

        if(shortLanguage && Array.isArray(buttonsByLanguage[shortLanguage])){
            return buttonsByLanguage[shortLanguage];
        }
    }

    return Array.isArray(fallback) ? fallback : [];
};

const languageSwitcherItems = (config = {}) => {
    const items = Array.isArray(config.languageSwitcher?.items)
        ? config.languageSwitcher.items
        : [];

    return items
        .filter((item) => item?.url && (item?.label || item?.code))
        .map((item) => ({
            code: normalizeLanguage(item.code || item.label),
            label: String(item.label || item.code || '').toUpperCase(),
            name: item.name || item.label || item.code || '',
            url: item.url,
            current: Boolean(item.current),
        }));
};

const splitNavigationItems = (items = []) => {
    if(!Array.isArray(items) || !items.length){
        return [[], []];
    }

    const splitIndex = Math.ceil(items.length / 2);

    return [
        items.slice(0, splitIndex),
        items.slice(splitIndex),
    ];
};

const itemDestination = (item = {}) => {
    const url = String(item.url || '').trim();
    const path = String(item.path || '').trim();

    if(url.startsWith('#')){
        return `/${url}`;
    }

    if(url && isExternal(url)){
        if(!isSameSiteUrl(url)){
            return url;
        }

        return sameSitePathFromUrl(url) || path || url;
    }

    return path || url || '/';
};

const navItemKey = (item = {}, index = 0, path = '') => {
    return String(item.id || `${path}-${index}-${item.label || item.title || ''}-${item.url || item.path || ''}`);
};

const renderHeaderLabel = (value = '') => parse(String(value ?? ''));

const MenuLink = ({ item, className = '', onClick }) => {
    const destination = itemDestination(item);
    const target = item.target || undefined;
    const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
    const label = renderHeaderLabel(item.label || item.title);

    if(isExternal(destination)){
        return (
            <a className={className} href={destination} target={target} rel={rel} onClick={onClick}>
                <span>{label}</span>
            </a>
        );
    }

    return (
        <AppLink className={className} to={destination} target={target} rel={rel} onClick={onClick}>
            <span>{label}</span>
        </AppLink>
    );
};

const HeaderPlainLink = ({ item, className = '', children, onClick }) => {
    const destination = item?.url || itemDestination(item);
    const target = item?.target || undefined;
    const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
    const content = children !== undefined && children !== null
        ? (typeof children === 'string' ? renderHeaderLabel(children) : children)
        : renderHeaderLabel(item?.label || item?.title);

    if(!destination){
        return null;
    }

    if(isExternal(destination)){
        return (
            <a className={className} href={destination} target={target} rel={rel} onClick={onClick}>
                {content}
            </a>
        );
    }

    return (
        <AppLink className={className} to={destination} target={target} rel={rel} onClick={onClick}>
            {content}
        </AppLink>
    );
};

const HeaderHomeLink = ({ to = '/', ...props }) => {
    const destination = String(to || '/').trim() || '/';

    if(isExternal(destination)){
        if(!isSameSiteUrl(destination)){
            return <a href={destination} {...props} />;
        }

        return <AppLink to={sameSitePathFromUrl(destination) || destination} {...props} />;
    }

    return <AppLink to={destination} {...props} />;
};

const LanguageSwitcher = ({ items = [], className = '', onItemClick }) => {
    const [isOpen, setIsOpen] = useState(false);
    const rootRef = useRef(null);
    const hasItems = Array.isArray(items) && items.length >= 2;
    const currentItem = hasItems ? (items.find((item) => item.current) || items[0]) : null;
    const currentValue = currentItem?.url || '';

    useEffect(() => {
        if(!isOpen || !hasItems){
            return undefined;
        }

        const handleDocumentClick = (event) => {
            if(rootRef.current && !rootRef.current.contains(event.target)){
                setIsOpen(false);
            }
        };

        const handleDocumentKeyDown = (event) => {
            if(event.key === 'Escape'){
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleDocumentClick);
        document.addEventListener('keydown', handleDocumentKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
            document.removeEventListener('keydown', handleDocumentKeyDown);
        };
    }, [hasItems, isOpen]);

    if(!hasItems){
        return null;
    }

    const handleToggle = () => {
        setIsOpen((open) => !open);
    };

    const handleItemClick = (event, item) => {
        if(!item?.url || item.url === currentValue){
            event.preventDefault();
            setIsOpen(false);
            return;
        }

        setIsOpen(false);

        if(typeof onItemClick === 'function'){
            onItemClick();
        }
    };

    return (
        <nav
            ref={rootRef}
            className={['zero-un-header__language-switcher', isOpen ? 'is-open' : '', className].filter(Boolean).join(' ')}
            aria-label="Langues"
        >
            <button
                type="button"
                className="zero-un-header__language-toggle"
                aria-label="Changer de langue"
                aria-expanded={isOpen}
                onClick={handleToggle}
            >
                <span className="zero-un-header__language-current">
                    {currentItem?.label || currentItem?.code}
                </span>
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="m6.5 9.5 5.5 5.5 5.5-5.5" />
                </svg>
            </button>
            <div className="zero-un-header__language-menu">
                {items.map((item, index) => {
                    const label = item.label || item.code;
                    const key = item.code || `${label}-${index}`;
                    const isCurrent = item.url === currentValue;

                    return (
                        <a
                            key={key}
                            className={['zero-un-header__language-item', isCurrent ? 'is-current' : ''].filter(Boolean).join(' ')}
                            href={item.url}
                            data-router="false"
                            aria-current={isCurrent ? 'true' : undefined}
                            onClick={(event) => handleItemClick(event, item)}
                        >
                            <span>{item.name || label}</span>
                        </a>
                    );
                })}
            </div>
        </nav>
    );
};

const DrawerNavigationList = ({ items = [], depth = 0, openItems = {}, onToggle, onItemClick, path = 'drawer' }) => {
    if(!Array.isArray(items) || !items.length){
        return null;
    }

    return (
        <ul className={depth > 0 ? 'zero-un-header__submenu-list' : 'zero-un-header__menu-list'}>
            {items.map((item, index) => {
                const children = Array.isArray(item.children) ? item.children : [];
                const hasChildren = children.length > 0;
                const itemKey = navItemKey(item, index, path);
                const isOpen = !!openItems[itemKey];
                const label = item.label || item.title;

                return (
                    <li
                        key={itemKey}
                        className={[
                            hasChildren ? 'has-children' : '',
                            isOpen ? 'is-open' : '',
                        ].filter(Boolean).join(' ')}
                    >
                        {hasChildren ? (
                            <button
                                type="button"
                                className={depth > 0 ? 'zero-un-header__submenu-link zero-un-header__submenu-toggle' : 'zero-un-header__menu-link zero-un-header__menu-toggle'}
                                aria-expanded={isOpen}
                                onClick={() => onToggle(itemKey)}
                            >
                                <span>{renderHeaderLabel(label)}</span>
                            </button>
                        ) : (
                            <MenuLink
                                item={item}
                                className={depth > 0 ? 'zero-un-header__submenu-link' : 'zero-un-header__menu-link'}
                                onClick={onItemClick}
                            />
                        )}
                        {hasChildren && isOpen ? (
                            <DrawerNavigationList
                                items={children}
                                depth={depth + 1}
                                openItems={openItems}
                                onToggle={onToggle}
                                onItemClick={onItemClick}
                                path={itemKey}
                            />
                        ) : null}
                    </li>
                );
            })}
        </ul>
    );
};

const drawerContactParts = (contact = {}) => {
    const text = typeof contact.text === 'string' ? contact.text.trim() : '';
    const links = Array.isArray(contact.links)
        ? contact.links.filter((link) => link?.label && link?.url)
        : [];
    const cta = contact.cta?.label && contact.cta?.url ? contact.cta : null;
    const secondaryCta = contact.secondaryCta?.label && contact.secondaryCta?.url ? contact.secondaryCta : null;

    return { text, links, cta, secondaryCta };
};

const DrawerContact = ({ text = '', links = [], languageItems = [], onItemClick }) => {
    if(!text && !links.length && (!Array.isArray(languageItems) || languageItems.length < 2)){
        return null;
    }

    return (
        <div className="zero-un-header__drawer-contact">
            {text ? (
                <div
                    className="zero-un-header__drawer-contact-text"
                    dangerouslySetInnerHTML={{ __html: text }}
                />
            ) : null}
            {links.length ? (
                <div className="zero-un-header__drawer-socials">
                    {links.map((link, index) => (
                        <HeaderPlainLink
                            key={`${link.url}-${index}`}
                            item={link}
                            onClick={onItemClick}
                        >
                            {link.label}
                        </HeaderPlainLink>
                    ))}
                </div>
            ) : null}
            <LanguageSwitcher
                items={languageItems}
                className="zero-un-header__language-switcher--drawer"
                onItemClick={onItemClick}
            />
        </div>
    );
};

const DrawerCtaIcon = ({ svg = '' }) => {
    const markup = getSafeSvgMarkup(svg);

    if(markup){
        return (
            <i
                className="has-custom-icon"
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: markup }}
            />
        );
    }

    return (
        <i aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
                <path d="M7 7h10v10M17 7 7 17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </i>
    );
};

const DrawerCta = ({ cta = null, onItemClick }) => {
    if(!cta){
        return null;
    }

    return (
        <HeaderPlainLink className="zero-un-header__drawer-cta" item={cta} onClick={onItemClick}>
            <span>{renderHeaderLabel(cta.label)}</span>
            <DrawerCtaIcon svg={cta.icon_svg} />
        </HeaderPlainLink>
    );
};

const DrawerFooter = ({ contact = {}, languageItems = [], onItemClick }) => {
    const { text, links, cta } = drawerContactParts(contact);

    if(!text && !links.length && !cta && (!Array.isArray(languageItems) || languageItems.length < 2)){
        return null;
    }

    return (
        <div className={['zero-un-header__drawer-footer', cta ? 'has-cta' : ''].filter(Boolean).join(' ')}>
            {text || links.length || (Array.isArray(languageItems) && languageItems.length > 1) ? (
                <DrawerContact text={text} links={links} languageItems={languageItems} onItemClick={onItemClick} />
            ) : (
                <span className="zero-un-header__drawer-contact zero-un-header__drawer-contact--empty" aria-hidden="true"></span>
            )}
            <DrawerCta cta={cta} onItemClick={onItemClick} />
        </div>
    );
};

const NavigationList = ({ items = [], depth = 0, onItemClick }) => {
    if(!Array.isArray(items) || !items.length){
        return null;
    }

    return (
        <ul className={depth > 0 ? 'zero-un-header__submenu-list' : 'zero-un-header__menu-list'}>
            {items.map((item) => {
                const children = Array.isArray(item.children) ? item.children : [];
                const hasChildren = children.length > 0;

                return (
                    <li
                        key={item.id || `${item.label}-${item.url}`}
                        className={hasChildren ? 'has-children' : ''}
                    >
                        <MenuLink
                            item={item}
                            className={depth > 0 ? 'zero-un-header__submenu-link' : 'zero-un-header__menu-link'}
                            onClick={onItemClick}
                        />
                        {hasChildren ? <NavigationList items={children} depth={depth + 1} onItemClick={onItemClick} /> : null}
                    </li>
                );
            })}
        </ul>
    );
};

const HeaderActions = ({ buttons = [] }) => {
    const visibleButtons = Array.isArray(buttons)
        ? buttons.filter((button) => button?.label && button?.url).slice(0, 2)
        : [];

    if(!visibleButtons.length){
        return null;
    }

    return (
        <div className="zero-un-header__actions">
            {visibleButtons.map((button, index) => {
                const target = button.target || undefined;

                return (
                    <Button
                        key={`${button.url}-${index}`}
                        className="zero-un-header__action"
                        href={button.url}
                        text={button.label}
                        variant={button.variant || 'primary'}
                        target={target}
                        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                    />
                );
            })}
        </div>
    );
};

const HamburgerIcon = () => (
    <span className="zero-un-header__hamburger-lines" aria-hidden="true">
        <span />
        <span />
    </span>
);

const Header = forwardRef(function Header({
    show = false,
    className = null,
    mountId = 'app-header',
    route = runtime.route,
    routeKey = runtime.route?.key || '',
    ...props
}, ref){
    
    const localRef = useRef(null);
    const lastScrollTopRef = useRef(0);
    const headerRef = useCallback((node) => {
        localRef.current = node;

        if(typeof ref === 'function'){
            ref(node);
        } else if(ref){
            ref.current = node;
        }
    }, [ref]);
    const drawerCloseTimeoutRef = useRef(null);
    const [menuOpen, menuOpenSet] = useState(false);
    const [drawerVisible, drawerVisibleSet] = useState(false);
    const [drawerClosing, drawerClosingSet] = useState(false);
    const [openDrawerItems, openDrawerItemsSet] = useState({});
    const [hiddenOnScroll, hiddenOnScrollSet] = useState(false);
    const config = runtime.system?.zeroUn?.header || {};
    const language = currentHeaderLanguage(config, route);
    const homeUrl = resolveHeaderHomeUrl(config, language);
    const { logo, mediaKey: logoMediaKey } = resolveHeaderLogo(config, language);
    const headerNavigationLocation = resolveNavigationLocation(config.navigationLocations, language, config.navigationLocation);
    const explicitHamburgerNavigationLocation = resolveNavigationLocation(config.hamburger?.navigationLocations, language, config.hamburger?.navigationLocation);
    const hamburgerNavigationLocation = explicitHamburgerNavigationLocation || headerNavigationLocation;
    const hasHamburgerNavigationLocation = hasNavigationLocation(hamburgerNavigationLocation);
    const headerButtons = useMemo(() => resolveHeaderButtons(config.buttonsByLanguage, language, config.buttons), [config.buttonsByLanguage, config.buttons, language]);
    const headerLanguageItems = useMemo(() => languageSwitcherItems(config), [config.languageSwitcher]);
    const variant = ['standard', 'centered', 'hamburger'].includes(config.variant) ? config.variant : 'standard';
    const headerNavigation = useMemo(() => resolveMenu(runtime.navigation, headerNavigationLocation), [headerNavigationLocation]);
    const hamburgerNavigation = useMemo(() => {
        const menu = resolveMenu(runtime.navigation, hamburgerNavigationLocation);

        if(menu.length || variant !== 'hamburger' || hasHamburgerNavigationLocation){
            return menu;
        }

        const fallbackMenu = resolveFirstMenu(runtime.navigation);

        return fallbackMenu.length ? fallbackMenu : [];
    }, [hamburgerNavigationLocation, hasHamburgerNavigationLocation, variant]);
    const [leftNavigation, rightNavigation] = useMemo(() => splitNavigationItems(headerNavigation), [headerNavigation]);
    const layout = config.boxed === true || config.boxed === 1 || config.boxed === '1' || config.layout === 'boxed' ? 'boxed' : 'full';
    const hasBorderRadius = config.borderRadius !== false && config.borderRadius !== 0 && config.borderRadius !== '0';
    const hamburgerVariantRaw = ['fullscreen', 'editorial'].includes(config.hamburger?.variant) ? 'spotlight' : config.hamburger?.variant;
    const hamburgerVariant = ['panel', 'spotlight'].includes(hamburgerVariantRaw) ? hamburgerVariantRaw : 'panel';
    const hamburgerBoxed = config.hamburger?.boxed !== false && config.hamburger?.boxed !== 0 && config.hamburger?.boxed !== '0';
    const hamburgerHasBorderRadius = config.hamburger?.borderRadius !== false && config.hamburger?.borderRadius !== 0 && config.hamburger?.borderRadius !== '0';
    const showStandardNavigation = variant === 'standard' && headerNavigation.length > 0;
    const showCenteredNavigation = variant === 'centered' && headerNavigation.length > 0;
    const showHeaderActions = variant === 'standard' && headerButtons.length > 0;
    const showHamburgerButton = variant === 'hamburger' || hasHamburgerNavigationLocation || hamburgerNavigation.length > 0;
    const showLanguageSwitcher = headerLanguageItems.length > 1;
    const showHeaderTools = showLanguageSwitcher || showHamburgerButton;

    useEffect(() => {
        const root = document.documentElement;
        const desktopFallback = layout === 'full' ? 80 : 120;
        const mobileFallback = layout === 'full' ? 72 : 100;
        let frame = null;

        root.style.setProperty('--zero-un-header-safe-area', `${desktopFallback}px`);
        root.style.setProperty('--zero-un-header-mobile-safe-area', `${mobileFallback}px`);

        const updateSafeArea = () => {
            frame = null;

            const header = localRef.current;

            if(!header){
                return;
            }

            const height = Math.ceil(header.getBoundingClientRect().height);

            if(height <= 0){
                return;
            }

            const value = `${height}px`;
            root.style.setProperty('--zero-un-header-safe-area', value);
            root.style.setProperty('--zero-un-header-mobile-safe-area', value);
        };

        const scheduleUpdate = () => {
            if(frame !== null){
                window.cancelAnimationFrame(frame);
            }

            frame = window.requestAnimationFrame(updateSafeArea);
        };

        scheduleUpdate();

        const observer = typeof ResizeObserver !== 'undefined' && localRef.current
            ? new ResizeObserver(scheduleUpdate)
            : null;

        observer?.observe(localRef.current);
        window.addEventListener('resize', scheduleUpdate);

        return () => {
            if(frame !== null){
                window.cancelAnimationFrame(frame);
            }

            observer?.disconnect();
            window.removeEventListener('resize', scheduleUpdate);
            root.style.removeProperty('--zero-un-header-safe-area');
            root.style.removeProperty('--zero-un-header-mobile-safe-area');
        };
    }, [layout, show, config.enabled, showHeaderActions, showHeaderTools, variant]);

    useEffect(() => {
        if(!show || config.enabled === false || typeof window === 'undefined'){
            return undefined;
        }

        let frame = null;
        let active = true;

        lastScrollTopRef.current = Math.max(0, scroller.getScrollTop());
        hiddenOnScrollSet(false);

        const updateVisibility = () => {
            if(!active){
                return;
            }

            const scrollTop = Math.max(0, scroller.getScrollTop());
            const delta = scrollTop - lastScrollTopRef.current;

            if(drawerVisible || scrollTop <= HEADER_SCROLL_TOP_THRESHOLD){
                hiddenOnScrollSet(false);
                lastScrollTopRef.current = scrollTop;
            }else if(Math.abs(delta) >= HEADER_SCROLL_DIRECTION_THRESHOLD){
                hiddenOnScrollSet(delta > 0);
                lastScrollTopRef.current = scrollTop;
            }

            frame = window.requestAnimationFrame(updateVisibility);
        };

        frame = window.requestAnimationFrame(updateVisibility);

        return () => {
            active = false;

            if(frame !== null){
                window.cancelAnimationFrame(frame);
            }
        };
    }, [show, config.enabled, drawerVisible, routeKey]);

    const clearDrawerCloseTimer = () => {
        if(drawerCloseTimeoutRef.current){
            window.clearTimeout(drawerCloseTimeoutRef.current);
            drawerCloseTimeoutRef.current = null;
        }
    };

    const openMenu = () => {
        clearDrawerCloseTimer();
        drawerVisibleSet(true);
        drawerClosingSet(false);
        menuOpenSet(true);
    };

    const closeMenu = () => {
        if(!menuOpen && !drawerVisible){
            return;
        }

        clearDrawerCloseTimer();
        menuOpenSet(false);
        drawerClosingSet(true);
        drawerCloseTimeoutRef.current = window.setTimeout(() => {
            drawerVisibleSet(false);
            drawerClosingSet(false);
            openDrawerItemsSet({});
            drawerCloseTimeoutRef.current = null;
        }, DRAWER_ANIMATION_DURATION);
    };

    const toggleMenu = () => {
        if(menuOpen){
            closeMenu();
            return;
        }

        openMenu();
    };

    const toggleDrawerItem = (itemKey) => {
        openDrawerItemsSet((current) => ({
            ...current,
            [itemKey]: !current[itemKey],
        }));
    };

    useEffect(() => {
        document.documentElement.classList.toggle('is-header-menu-open', drawerVisible);
        document.body.classList.toggle('is-header-menu-open', drawerVisible);

        if(drawerVisible){
            scroller.pauseSmooth('header-menu');
        } else {
            scroller.resumeSmooth('header-menu');
        }

        return () => {
            document.documentElement.classList.remove('is-header-menu-open');
            document.body.classList.remove('is-header-menu-open');
            scroller.resumeSmooth('header-menu');
        };
    }, [drawerVisible]);

    useEffect(() => {
        clearDrawerCloseTimer();
        menuOpenSet(false);
        drawerVisibleSet(false);
        drawerClosingSet(false);
        openDrawerItemsSet({});
    }, [headerNavigationLocation, hamburgerNavigationLocation]);

    useEffect(() => {
        return () => {
            clearDrawerCloseTimer();
        };
    }, []);

    if(!show || config.enabled === false){
        return null;
    }

    const mountNode = typeof document !== 'undefined'
        ? (document.getElementById(mountId) || document.body)
        : null;

    if(!mountNode){
        return null;
    }

    const tagProps = {
        className: [
            'zero-un-header',
            `zero-un-header--${variant}`,
            `zero-un-header--${layout}`,
            hasBorderRadius ? 'zero-un-header--rounded' : '',
            showHeaderActions ? 'zero-un-header--has-actions' : '',
            menuOpen ? 'is-menu-open' : '',
            hiddenOnScroll && !drawerVisible ? 'is-hidden-on-scroll' : '',
            className,
        ].filter(Boolean).join(' '),
        style: styleVariables(config),
        ...props
    };
    const hamburgerContact = config.hamburger?.contact || {};
    const hamburgerContactParts = drawerContactParts(hamburgerContact);
    const drawerLanguageItems = showLanguageSwitcher ? [] : headerLanguageItems;
    const headerToolsClassName = [
        'zero-un-header__tools',
        showLanguageSwitcher ? 'zero-un-header__tools--has-language' : '',
        showHamburgerButton ? 'zero-un-header__tools--has-hamburger' : '',
    ].filter(Boolean).join(' ');

    return createPortal(
        <header ref={headerRef} {...tagProps}>
            <div className="zero-un-header__bar">
                <HeaderHomeLink className="zero-un-header__brand" to={homeUrl} aria-label={runtime.site?.name || 'Accueil'}>
                    {logo?.url ? (
                        <Image
                            className="zero-un-header__brand-logo"
                            data-media-key={logoMediaKey}
                            aria-label={logo.alt || runtime.site?.name || ''}
                        />
                    ) : (
                        <span>{runtime.site?.name || 'Site'}</span>
                    )}
                </HeaderHomeLink>

                {showStandardNavigation ? (
                    <nav className="zero-un-header__menu" aria-label="Navigation principale">
                        <NavigationList items={headerNavigation} />
                    </nav>
                ) : null}

                {showCenteredNavigation ? (
                    <>
                        <nav className="zero-un-header__menu zero-un-header__menu--left" aria-label="Navigation principale gauche">
                            <NavigationList items={leftNavigation} />
                        </nav>
                        <nav className="zero-un-header__menu zero-un-header__menu--right" aria-label="Navigation principale droite">
                            <NavigationList items={rightNavigation} />
                        </nav>
                    </>
                ) : null}

                {showHeaderActions ? <HeaderActions buttons={headerButtons} /> : null}

                {showHeaderTools ? (
                    <div className={headerToolsClassName}>
                        <LanguageSwitcher
                            items={headerLanguageItems}
                            className="zero-un-header__language-switcher--header"
                        />
                        {showHamburgerButton ? (
                            <button
                                type="button"
                                className="zero-un-header__hamburger"
                                aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                                aria-expanded={menuOpen}
                                onClick={toggleMenu}
                            >
                                <HamburgerIcon />
                            </button>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {showHamburgerButton && drawerVisible ? (
                <div
                    className={[
                        'zero-un-header__drawer',
                        `zero-un-header__drawer--${hamburgerVariant}`,
                        hamburgerBoxed ? 'zero-un-header__drawer--boxed' : 'zero-un-header__drawer--full',
                        hamburgerHasBorderRadius ? 'zero-un-header__drawer--rounded' : '',
                        drawerClosing ? 'is-closing' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={closeMenu}
                >
                    <div className="zero-un-header__drawer-inner" onClick={(event) => event.stopPropagation()}>
                        {hamburgerVariant === 'spotlight' ? (
                            <div className="zero-un-header__drawer-card">
                                <span className="zero-un-header__drawer-rail" aria-hidden="true"></span>
                                <nav className="zero-un-header__drawer-menu" aria-label="Menu">
                                    <DrawerNavigationList
                                        items={hamburgerNavigation}
                                        openItems={openDrawerItems}
                                        onToggle={toggleDrawerItem}
                                        onItemClick={closeMenu}
                                    />
                                </nav>
                                <DrawerFooter contact={config.hamburger?.contact} languageItems={drawerLanguageItems} onItemClick={closeMenu} />
                            </div>
                        ) : (
                            <>
                                <div className="zero-un-header__drawer-panel-card">
                                    <nav className="zero-un-header__drawer-menu" aria-label="Menu">
                                        <DrawerNavigationList
                                            items={hamburgerNavigation}
                                            openItems={openDrawerItems}
                                            onToggle={toggleDrawerItem}
                                            onItemClick={closeMenu}
                                        />
                                    </nav>
                                    {hamburgerContactParts.cta || hamburgerContactParts.secondaryCta ? (
                                        <div
                                            className={[
                                                'zero-un-header__drawer-cta-grid',
                                                hamburgerContactParts.cta && hamburgerContactParts.secondaryCta ? 'has-multiple' : '',
                                            ].filter(Boolean).join(' ')}
                                        >
                                            <DrawerCta cta={hamburgerContactParts.cta} onItemClick={closeMenu} />
                                            <DrawerCta cta={hamburgerContactParts.secondaryCta} onItemClick={closeMenu} />
                                        </div>
                                    ) : null}
                                </div>
                                {(hamburgerContactParts.text || hamburgerContactParts.links.length || drawerLanguageItems.length > 1) ? (
                                    <div className="zero-un-header__drawer-footer zero-un-header__drawer-footer--panel">
                                        <DrawerContact
                                            text={hamburgerContactParts.text}
                                            links={hamburgerContactParts.links}
                                            languageItems={drawerLanguageItems}
                                            onItemClick={closeMenu}
                                        />
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                </div>
            ) : null}
        </header>,
        mountNode
    );
});

export default Header;
